/**
 * Toast & Notification System
 *
 * Singleton-based toast manager exposed via `window.Toast`.
 * Uses a custom element `<toast-container>` for the overlay stack,
 * and plain DOM nodes for each individual toast.
 *
 * Design pattern: Singleton + Observer (CustomEvent pub/sub).
 *
 * @example
 *   window.Toast.success('Item added to cart!');
 *   window.Toast.error('Payment failed.');
 *   window.Toast.warning('Stock is low.');
 *   window.Toast.info('New features available.');
 *
 *   window.Toast.show({
 *     state: 'error',
 *     type: 'notification',
 *     title: 'Payment failed',
 *     message: 'Your card was declined.',
 *     action: { label: 'Try again', url: '/checkout' },
 *     duration: 0, // persistent
 *   });
 *
 *   window.Toast.dismiss(id);
 *   window.Toast.dismissAll();
 */

/** @typedef {'success'|'error'|'warning'|'info'} ToastState */
/** @typedef {'toast'|'notification'} ToastType */

/**
 * @typedef {Object} ToastAction
 * @property {string} label - Action link text
 * @property {string} [url] - Action link URL
 * @property {Function} [callback] - Action callback (used instead of URL)
 */

/**
 * @typedef {Object} ToastOptions
 * @property {string} message - The toast body text
 * @property {ToastState} [state='info'] - Visual state
 * @property {ToastType} [type='toast'] - Toast or notification
 * @property {string} [title] - Optional title (notification only)
 * @property {ToastAction} [action] - Optional action link (notification only)
 * @property {boolean} [dismissible] - Show close button (default: true for notification, false for toast)
 * @property {number} [duration=5000] - Auto-dismiss duration in ms (0 = persistent)
 * @property {string} [position='bottom-center'] - Container position
 */

const ICON_MAP = /** @type {Record<ToastState, string>} */ ({
  error: 'warning',
  success: 'check-circle',
  warning: 'warning',
  info: 'info',
});

const DEFAULT_DURATION = 5000;
const EXIT_ANIMATION_MS = 200;
const MAX_VISIBLE_TOASTS = 5;

let toastIdCounter = 0;

/**
 * Retrieves or creates the SVG markup for a given icon name.
 * Falls back to inline SVG content already present in the page via icon.liquid.
 * @param {string} iconName
 * @returns {string}
 */
const getIconSvg = (iconName) => {
  const existing = document.querySelector(`.icon svg[class*="${iconName}"]`)
    || document.querySelector(`.toast__icon .icon svg`);

  const templateIcon = document.querySelector(`[data-toast-icon="${iconName}"]`);
  if (templateIcon) return templateIcon.innerHTML;

  if (existing) return existing.outerHTML;

  return '';
};

class ToastContainer extends HTMLElement {
  /** @type {Map<string, {element: HTMLElement, timer: number|null}>} */
  #toasts = new Map();

  #controller = new AbortController();

  connectedCallback() {
    if (!this.getAttribute('position')) {
      this.setAttribute('position', 'bottom-center');
    }

    this.setAttribute('aria-live', 'polite');
    this.setAttribute('aria-relevant', 'additions');
    this.setAttribute('role', 'status');
  }

  disconnectedCallback() {
    this.#controller.abort();

    for (const [id] of this.#toasts) {
      this.dismiss(id, true);
    }
  }

  /**
   * Creates and shows a new toast.
   * @param {ToastOptions} options
   * @returns {string} The toast ID
   */
  show(options) {
    const id = `toast-${++toastIdCounter}`;
    const state = options.state || 'info';
    const type = options.type || 'toast';
    const duration = options.duration ?? (type === 'toast' ? DEFAULT_DURATION : 0);
    const dismissible = options.dismissible ?? (type === 'notification');

    const el = this.#buildToastElement(id, { ...options, state, type, dismissible });

    if (this.#toasts.size >= MAX_VISIBLE_TOASTS) {
      const oldestId = this.#toasts.keys().next().value;
      this.dismiss(oldestId, true);
    }

    /** @type {number|null} */
    let timer = null;

    if (duration > 0) {
      timer = window.setTimeout(() => this.dismiss(id), duration);
    }

    this.#toasts.set(id, { element: el, timer });
    this.appendChild(el);

    if (duration > 0) {
      const { signal } = this.#controller;

      el.addEventListener('mouseenter', () => this.#pauseTimer(id), { signal });
      el.addEventListener('mouseleave', () => this.#resumeTimer(id, duration), { signal });
      el.addEventListener('focusin', () => this.#pauseTimer(id), { signal });
      el.addEventListener('focusout', () => this.#resumeTimer(id, duration), { signal });
    }

    document.dispatchEvent(new CustomEvent('toast:show', { detail: { id, ...options } }));

    return id;
  }

  /**
   * Dismisses a toast by ID.
   * @param {string} id
   * @param {boolean} [immediate=false]
   */
  dismiss(id, immediate = false) {
    const entry = this.#toasts.get(id);
    if (!entry) return;

    if (entry.timer) {
      clearTimeout(entry.timer);
    }

    if (immediate) {
      entry.element.remove();
      this.#toasts.delete(id);
      document.dispatchEvent(new CustomEvent('toast:dismiss', { detail: { id } }));
      return;
    }

    entry.element.classList.add('toast--exiting');

    setTimeout(() => {
      entry.element.remove();
      this.#toasts.delete(id);
      document.dispatchEvent(new CustomEvent('toast:dismiss', { detail: { id } }));
    }, EXIT_ANIMATION_MS);
  }

  dismissAll() {
    for (const [id] of this.#toasts) {
      this.dismiss(id);
    }
  }

  /**
   * @param {string} id
   * @param {ToastOptions & {dismissible: boolean}} options
   * @returns {HTMLElement}
   */
  #buildToastElement(id, options) {
    const { state, type, title, message, action, dismissible } = options;

    const el = document.createElement('div');
    el.className = `toast toast--${state} toast--${type}`;
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'polite');
    el.dataset.toastId = id;

    const iconName = ICON_MAP[state ?? 'info'];

    let html = `<span class="toast__icon" aria-hidden="true">${this.#getIconMarkup(iconName)}</span>`;

    if (type === 'notification') {
      html += '<div class="toast__container"><div class="toast__content">';

      if (title) {
        html += `<p class="toast__title">${this.#escapeHtml(title)}</p>`;
      }

      html += `<p class="toast__message">${this.#escapeHtml(message)}</p>`;
      html += '</div>';

      if (action) {
        if (action.url) {
          html += `<a class="toast__action" href="${this.#escapeHtml(action.url)}">${this.#escapeHtml(action.label)}</a>`;
        } else {
          html += `<button class="toast__action" type="button">${this.#escapeHtml(action.label)}</button>`;
        }
      }

      html += '</div>';
    } else {
      html += `<p class="toast__message">${this.#escapeHtml(message)}</p>`;
    }

    if (dismissible) {
      html += `<button class="toast__close" type="button" aria-label="Close">${this.#getIconMarkup('x')}</button>`;
    }

    el.innerHTML = html;

    if (dismissible) {
      const closeBtn = el.querySelector('.toast__close');
      closeBtn?.addEventListener('click', () => this.dismiss(id));
    }

    if (action?.callback) {
      const actionEl = el.querySelector('.toast__action');
      actionEl?.addEventListener('click', (e) => {
        if (!action.url) e.preventDefault();
        action.callback?.();
      });
    }

    return el;
  }

  /**
   * Gets icon markup, preferring to clone from existing DOM icons rendered by icon.liquid.
   * @param {string} iconName
   * @returns {string}
   */
  #getIconMarkup(iconName) {
    const iconTemplate = document.querySelector(`[data-toast-icon="${iconName}"]`);
    if (iconTemplate) return iconTemplate.innerHTML;

    const existingIcon = document.querySelector(`.toast__icon .icon`);
    if (existingIcon) {
      const clone = /** @type {HTMLElement} */ (existingIcon.cloneNode(true));
      return clone.outerHTML;
    }

    return `<span class="icon icon--md" style="width:1.25rem;height:1.25rem;display:inline-flex;align-items:center;justify-content:center;"></span>`;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /** @param {string} id */
  #pauseTimer(id) {
    const entry = this.#toasts.get(id);
    if (!entry?.timer) return;

    clearTimeout(entry.timer);
    entry.timer = null;
  }

  /**
   * @param {string} id
   * @param {number} duration
   */
  #resumeTimer(id, duration) {
    const entry = this.#toasts.get(id);
    if (!entry || entry.timer) return;

    entry.timer = window.setTimeout(() => this.dismiss(id), duration);
  }
}

if (!customElements.get('toast-container')) {
  customElements.define('toast-container', ToastContainer);
}

// ─── Global Toast API (Singleton) ───────────────────────────────────────────

/** @returns {ToastContainer} */
const getContainer = (position = 'bottom-center') => {
  let container = /** @type {ToastContainer|null} */ (
    document.querySelector(`toast-container[position="${position}"]`)
  );

  if (!container) {
    container = /** @type {ToastContainer} */ (document.createElement('toast-container'));
    container.setAttribute('position', position);
    document.body.appendChild(container);
  }

  return container;
};

const ToastAPI = {
  /**
   * Show a toast with full options.
   * @param {ToastOptions} options
   * @returns {string} Toast ID
   */
  show(options) {
    const container = getContainer(options.position);
    return container.show(options);
  },

  /**
   * @param {string} message
   * @param {Partial<ToastOptions>} [options]
   * @returns {string}
   */
  success(message, options = {}) {
    return this.show({ ...options, message, state: 'success' });
  },

  /**
   * @param {string} message
   * @param {Partial<ToastOptions>} [options]
   * @returns {string}
   */
  error(message, options = {}) {
    return this.show({ ...options, message, state: 'error' });
  },

  /**
   * @param {string} message
   * @param {Partial<ToastOptions>} [options]
   * @returns {string}
   */
  warning(message, options = {}) {
    return this.show({ ...options, message, state: 'warning' });
  },

  /**
   * @param {string} message
   * @param {Partial<ToastOptions>} [options]
   * @returns {string}
   */
  info(message, options = {}) {
    return this.show({ ...options, message, state: 'info' });
  },

  /**
   * Dismiss a specific toast.
   * @param {string} id
   */
  dismiss(id) {
    for (const container of document.querySelectorAll('toast-container')) {
      /** @type {ToastContainer} */ (container).dismiss(id);
    }
  },

  /** Dismiss all toasts. */
  dismissAll() {
    for (const container of document.querySelectorAll('toast-container')) {
      /** @type {ToastContainer} */ (container).dismissAll();
    }
  },
};

// @ts-ignore
window.Toast = ToastAPI;

export { ToastAPI, ToastContainer };
